export const stripNonDigits = (value: string) => String(value || "").replace(/\D/g, "");

// Lowercase connectors that should stay lowercase inside a name (de Souza, das
// Neves, etc). The first word is always capitalized regardless.
const NAME_CONNECTORS = new Set(["de", "da", "do", "das", "dos", "e"]);

/**
 * Validates that a person/company name has at least 2 words. Single-word
 * entries like "João" or "Maria" are too generic to be useful in a CRM —
 * we want the full name. Returns an error message string for use as a
 * react-admin validator (returns `undefined` when valid).
 */
export const validateFullName = (value: unknown): string | undefined => {
  const text = typeof value === "string" ? value : "";
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return undefined; // `required()` handles empty
  if (words.length < 2) return "Informe o nome completo (pelo menos nome e sobrenome)";
  return undefined;
};

/**
 * Title-case a person/company name. "joão DA silva" → "João da Silva".
 * Connectors stay lowercase except when they are the first word.
 */
export const capitalizeName = (value: string | null | undefined): string => {
  if (typeof value !== "string") return "";
  const trimmed = value.replace(/\s+/g, " ").trim();
  if (!trimmed) return "";
  return trimmed
    .split(" ")
    .map((word, idx) => {
      const lower = word.toLowerCase();
      if (idx > 0 && NAME_CONNECTORS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

/**
 * Normaliza um nome de pessoa/empresa para UPPER CASE, colapsando espaços.
 * "joão  da   silva" → "JOÃO DA SILVA". Espelha o trigger normalize_upper_name
 * no banco (migration 69), que é a fonte da verdade na persistência — aqui é só
 * pra UI otimista bater com o que será salvo.
 */
export const upperName = (value: string | null | undefined): string => {
  if (typeof value !== "string") return "";
  return value.replace(/\s+/g, " ").trim().toUpperCase();
};

export const formatCpf = (value: string) => {
  const digits = stripNonDigits(value).slice(0, 11);
  if (!digits) return "";
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

export const formatCnpj = (value: string) => {
  const digits = stripNonDigits(value).slice(0, 14);
  if (!digits) return "";
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

export const formatDocument = (value: string, personType: string) => {
  return personType === "PJ" ? formatCnpj(value) : formatCpf(value);
};

export const isRepeatedDigits = (value: string) => /^([0-9])\1+$/.test(value);

export const isValidCpf = (cpf: string) => {
  const digits = stripNonDigits(cpf);
  if (digits.length !== 11 || isRepeatedDigits(digits)) return false;

  const calc = (base: string, factor: number) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * (factor - i);
    }
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };

  const digit1 = calc(digits.slice(0, 9), 10);
  const digit2 = calc(digits.slice(0, 10), 11);
  return digits.endsWith(`${digit1}${digit2}`);
};

export const isValidCnpj = (cnpj: string) => {
  const digits = stripNonDigits(cnpj);
  if (digits.length !== 14 || isRepeatedDigits(digits)) return false;

  const calc = (base: string, factors: number[]) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * factors[i];
    }
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const factors1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const factors2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const digit1 = calc(digits.slice(0, 12), factors1);
  const digit2 = calc(digits.slice(0, 13), factors2);
  return digits.endsWith(`${digit1}${digit2}`);
};
